import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, Check } from 'lucide-react';
import useConfiguratorStore from '../store/configuratorStore';
import QuantitySelector from './QuantitySelector';
import { getProductImageSrc } from '../utils/imageUtils';

const Section = styled.div`
  margin-bottom: 2.5rem;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: ${props => props.$isExpanded ? '1.5rem' : '0'};
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--gray-200);
  border-radius: var(--rounded-md);
  padding: 1rem;
  transition: all 0.3s ease;
  cursor: pointer;
  background: ${props => props.$highlighted ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(59, 130, 246, 0.03) 100%)' : 'transparent'};

  &:hover {
    background: ${props => props.$highlighted ?
      'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(59, 130, 246, 0.05) 100%)' :
      'var(--gray-50)'};
  }
`;

const ChevronIcon = styled(ChevronRight)`
  width: 24px;
  height: 24px;
  color: var(--gray-500);
  transition: transform 0.3s ease;
  transform: ${props => props.$isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'};
`;

const AccordionContent = styled(motion.div)`
  overflow: visible;
`;

const SectionNumber = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${props => {
    if (props.$completed) return 'var(--success-color, #22c55e)';
    return 'var(--primary-color)';
  }};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: var(--text-md);
  transition: all 0.3s ease;
`;

const SectionTitle = styled.h2`
  font-size: var(--text-xl);
  font-weight: 600;
  color: var(--secondary-color);
  flex: 1;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const ProductCard = styled(motion.div)`
  cursor: pointer;
  background-color: white;
  border-radius: var(--rounded-lg);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  border: 2px solid ${props => props.selected ? 'var(--primary-color)' : 'var(--gray-200)'};
  transition: all 0.2s ease;
  position: relative;
  height: 220px;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-md);
    border-color: ${props => props.selected ? 'var(--primary-color)' : 'var(--gray-300)'};
  }
`;

// Define EnlargeButton first so it can be referenced in other styled components
const EnlargeButton = styled(motion.button)`
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  border-radius: 6px;
  width: 28px;
  height: 28px;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
    transform: scale(1.1);
  }
`;

const ProductImageContainer = styled.div`
  height: 140px;
  background-color: var(--gray-50);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  border-bottom: 1px solid var(--gray-200);
  position: relative;
  overflow: hidden;
  
  &:hover ${EnlargeButton} {
    opacity: 1;
  }
`;

const ProductImage = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease;
  }
  
  ${ProductCard}:hover & img {
    transform: scale(1.05);
  }
  
  /* Re-applying scaling rules */
  /* Make Manual Dosed Discharger image larger */
  img[src="https://i.imgur.com/RA49zez.png"] {
    transform: scale(1.2);
  }
  ${ProductCard}:hover & img[src="https://i.imgur.com/RA49zez.png"] {
    transform: scale(1.25);
  }
  /* Make single Manual Dosed Discharger image larger */
  img[src="https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/Spare%20Parts/Manual%20Dosed%20Discharger.avif"] {
    transform: scale(1.1);
  }
  ${ProductCard}:hover & img[src="https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/Spare%20Parts/Manual%20Dosed%20Discharger.avif"] {
    transform: scale(1.15);
  }
  /* Make Add-on Carrier image larger */
  img[src="https://i.imgur.com/Ogvkpbg.png"] {
    transform: scale(1.5);
  }
  ${ProductCard}:hover & img[src="https://i.imgur.com/Ogvkpbg.png"] {
    transform: scale(1.55);
  }
  /* Make Filling Support Bars image larger */
  img[src="https://i.imgur.com/UBZbSmr.png"] {
    transform: scale(1.3);
  }
  ${ProductCard}:hover & img[src="https://i.imgur.com/UBZbSmr.png"] {
    transform: scale(1.35);
  }
  /* Make Stainless Steel Filling Support Bars image larger */
  img[src="https://i.imgur.com/u4fRjbj.png"] {
    transform: scale(1.3);
  }
  ${ProductCard}:hover & img[src="https://i.imgur.com/u4fRjbj.png"] {
    transform: scale(1.35);
  }
  /* Make Stainless Steel Add-on Carrier image larger */
  img[src="https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/Spare%20Parts/Carrier%20Stainless.avif"] {
    transform: scale(1.1);
  }
  ${ProductCard}:hover & img[src="https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/Spare%20Parts/Carrier%20Stainless.avif"] {
    transform: scale(1.15);
  }
  /* Make Stainless Steel Base Frame image larger */
  img[src="https://i.imgur.com/xwh7FSY.png"] {
    transform: scale(1.1);
  }
  ${ProductCard}:hover & img[src="https://i.imgur.com/xwh7FSY.png"] {
    transform: scale(1.15);
 }
 /* Make S.S. Feet Set image 10% larger */
 img[src="https://i.imgur.com/Hg5jDU9.png"] {
   transform: scale(1.1);
 }
 ${ProductCard}:hover & img[src="https://i.imgur.com/Hg5jDU9.png"] {
   transform: scale(1.15); /* Slightly larger on hover */
 }
  }
`;

const BadgeContainer = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  align-items: flex-end;
`;

const Badge = styled.span`
  background-color: ${props => {
    if (props.type === 'frame') return '#e0f2ff';
    if (props.type === 'feet') return '#fff8e0';
    if (props.type === 'addon') return '#ffe0e0';
    if (props.type === 'stainless') return '#e0f7ff';
    return 'var(--gray-200)';
  }};
  color: ${props => {
    if (props.type === 'frame') return '#0077cc';
    if (props.type === 'feet') return '#cc7700';
    if (props.type === 'addon') return '#cc0000';
    if (props.type === 'stainless') return '#0099cc';
    return 'var(--gray-700)';
  }};
  font-size: ${props => props.type === 'stainless' ? '0.6rem' : '0.65rem'}; /* Smaller font for stainless */
  font-weight: 600;
  padding: ${props => props.type === 'stainless' ? '0.25rem 0.5rem' : '0.25rem 0.5rem'}; /* Increased padding for stainless */
  border-radius: var(--rounded-sm);
  white-space: ${props => props.type === 'stainless' ? 'normal' : 'nowrap'}; /* Allow wrapping for stainless */
  line-height: ${props => props.type === 'stainless' ? '1.3' : 'normal'}; /* Keep line-height for stainless */
  max-width: ${props => props.type === 'stainless' ? '6em' : 'none'}; /* Further increased max-width for stainless */
  text-align: ${props => props.type === 'stainless' ? 'center' : 'left'}; /* Center text for stainless */
  display: ${props => props.type === 'stainless' ? 'inline-block' : 'inline'}; /* Use inline-block for stainless */
`;

const ProductInfo = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  flex: 1;
  position: relative;
`;

const ProductName = styled.h3`
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--gray-800);
  margin-bottom: 0.25rem;
  line-height: 1.3;
`;

const ProductPrice = styled.div`
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--primary-color);
  margin-top: auto;
  
  span {
    font-size: 0.85em;
    opacity: 0.7;
    margin-right: 4px;
  }
`;

const SelectedCheck = styled(motion.div)`
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  z-index: 5;
`;

const QuickAddButton = styled(motion.button)`
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${props => props.added ? '#22c55e' : '#16a34a'};
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  z-index: 4;
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
  transition: all 0.4s ease;
  opacity: ${props => props.hovered ? 1 : 0.8};
  line-height: 1;
  text-align: center;
  
  &:hover {
    background: #15803d;
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(34, 197, 94, 0.4);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const ProductSummary = styled.div`
  background-color: white;
  border-radius: var(--rounded-lg);
  box-shadow: var(--shadow-md);
  margin-top: 2rem;
  margin-bottom: 2rem;
  overflow: hidden;
`;

const SummaryHeader = styled.div`
  background-color: var(--gray-50);
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--gray-200);
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const SummaryTitle = styled.h3`
  font-size: var(--text-lg);
  font-weight: 600;
  color: var(--secondary-color);
`;

const SummaryContent = styled.div`
  padding: 1.5rem;
`;

const ProductDetails = styled.div`
  display: flex;
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  
  &:not(:last-of-type) {
    border-bottom: 1px solid var(--gray-200);
  }
`;

const DetailImage = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  background-color: var(--gray-50);
  border-radius: var(--rounded-md);
  padding: 0.5rem;
  border: 1px solid var(--gray-200);
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover ${EnlargeButton} {
    opacity: 1;
  }
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
  
  /* Re-applying scaling rules */
  /* Make Manual Dosed Discharger image larger in summary */
  img[src="https://i.imgur.com/RA49zez.png"] {
    transform: scale(1.2);
  }
  /* Make single Manual Dosed Discharger image larger in summary */
  img[src="https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/Spare%20Parts/Manual%20Dosed%20Discharger.avif"] {
    transform: scale(1.1);
  }
  /* Make Add-on Carrier image larger in summary */
  img[src="https://i.imgur.com/Ogvkpbg.png"] {
    transform: scale(1.5);
  }
  /* Make Filling Support Bars image larger in summary */
  img[src="https://i.imgur.com/UBZbSmr.png"] {
    transform: scale(1.3);
  }
  /* Make Stainless Steel Filling Support Bars image larger in summary */
  img[src="https://i.imgur.com/u4fRjbj.png"] {
    transform: scale(1.3);
  }
  /* Make Stainless Steel Add-on Carrier image larger in summary */
 img[src="https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/Spare%20Parts/Carrier%20Stainless.avif"] {
   transform: scale(1.1);
 }
 /* Make S.S. Feet Set image 10% larger in summary */
 img[src="https://i.imgur.com/Hg5jDU9.png"] {
   transform: scale(1.1);
 }
  /* Make Stainless Steel Base Frame image larger in summary */
  img[src="https://i.imgur.com/xwh7FSY.png"] {
    transform: scale(1.1);
  }
`;

const DetailInfo = styled.div`
  flex: 1;
  padding-left: 1.5rem;
`;

const DetailName = styled.h4`
  font-size: var(--text-lg);
  font-weight: 600;
  color: var(--gray-900);
  margin-bottom: 0.5rem;
`;

const DetailCode = styled.div`
  font-size: var(--text-xs);
  color: var(--gray-600);
  background-color: var(--gray-100);
  padding: 0.25rem 0.5rem;
  border-radius: var(--rounded-sm);
  display: inline-block;
  margin-bottom: 0.5rem;
`;

const FeatureList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const Feature = styled.span`
  font-size: var(--text-xs);
  color: var(--gray-700);
  background-color: var(--gray-100);
  padding: 0.25rem 0.5rem;
  border-radius: var(--rounded-sm);
  display: inline-flex;
  align-items: center;
  
  &:before {
    content: '•';
    margin-right: 0.25rem;
    color: var(--primary-color);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--gray-500);
`;

const DependencyMessage = styled(motion.div)`
  background-color: #f0f9ff;
  border: 1px solid #0ea5e9;
  border-radius: var(--rounded-lg);
  padding: 1rem 1.5rem;
  margin: 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: var(--text-sm);
  color: #0c4a6e;
  font-weight: 500;
`;

const GuidanceIcon = styled.div`
  font-size: 1.25rem;
  flex-shrink: 0;
`;

const MessageText = styled.div`
  flex: 1;
`;

const ConfigurationStatus = styled.div`
  background-color: ${props => {
    if (props.type === 'incomplete') return '#fef3c7';
    if (props.type === 'complete') return '#d1fae5';
    return '#f3f4f6';
  }};
  border: 1px solid ${props => {
    if (props.type === 'incomplete') return '#f59e0b';
    if (props.type === 'complete') return '#10b981';
    return '#d1d5db';
  }};
  border-radius: var(--rounded-md);
  padding: 0.75rem 1rem;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: var(--text-sm);
  color: ${props => {
    if (props.type === 'incomplete') return '#92400e';
    if (props.type === 'complete') return '#065f46';
    return '#374151';
  }};
`;

const StatusIcon = styled.div`
  font-size: 1rem;
  flex-shrink: 0;
`;

const StatusText = styled.div`
  flex: 1;
  font-weight: 500;
`;

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount);
};

// Extract product features from the name
const extractProductFeatures = (name) => {
  const features = [];
  
  if (name.includes('Frame Base')) features.push('Frame Base');
  if (name.includes('Feet set')) features.push('Feet Set');
  if (name.includes('Add-on')) features.push('Add-on');
  if (name.includes('Stainless Steel')) features.push('Stainless Steel');
  if (name.includes('Small')) features.push('Small');
  if (name.includes('Medium')) features.push('Medium');
  if (name.includes('Large')) features.push('Large');
  if (name.match(/WH\s*\d+[-]\d+mm/)) {
    const match = name.match(/WH\s*(\d+[-]\d+)mm/);
    if (match) features.push(`Height Range: ${match[1]}mm`);
  }
  
  return features;
};

// Image Modal Components
const ImageModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(8px);
`;

const ImageModalContent = styled(motion.div)`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
`;

const FullscreenImage = styled.img`
  width: 100%;
  height: 100%;
  max-width: 80vw;
  max-height: 80vh;
  object-fit: contain;
  display: block;
`;

const ImageModalHeader = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  color: white;
  padding: 2rem 1.5rem 1.5rem;
  text-align: center;
`;

const ImageModalTitle = styled.h3`
  font-size: var(--text-lg);
  font-weight: 600;
  margin: 0 0 0.5rem;
`;

const ImageModalCode = styled.p`
  font-size: var(--text-sm);
  opacity: 0.8;
  margin: 0;
`;

const ImageModalCloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
    transform: scale(1.1);
  }
`;

function UnloadingStationConfigurator() {
  const {
    organizedProducts,
    selectedMainCategory,
    selectedProduct,
    setSelectedProduct,
    quickAddToQuote,
    calculateQuote,
    priceMode
  } = useConfiguratorStore();

  const [products, setProducts] = useState([]);
  const [frameBases, setFrameBases] = useState([]);
  const [feetSets, setFeetSets] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [automationAddOns, setAutomationAddOns] = useState([]);
  const [quickAddStates, setQuickAddStates] = useState({});
  const [hoveredCard, setHoveredCard] = useState(null);
  const [imageModalData, setImageModalData] = useState(null);
  const [expandedSections, setExpandedSections] = useState(new Set([1]));

  // Ref for auto-scrolling to product summary
  const productSummaryRef = useRef(null);

  // Initialize and group products
  useEffect(() => {
    if (selectedMainCategory === 'unloading_stations' && organizedProducts?.unloading_stations) {
      const allProducts = organizedProducts.unloading_stations;
      setProducts(allProducts);
      
      // Group products by type
      setFrameBases(allProducts.filter(p => p.attributes.type === 'Frame Base'));
      setFeetSets(allProducts.filter(p => p.attributes.type === 'Feet Set'));
      
      // Filter add-ons and categorize them
      const allAddOns = allProducts.filter(p =>
        p.attributes.type === 'Add-on' ||
        (p.category && p.category.includes('Add-on'))
      );

      // Separate add-ons by category
      const regularAddOns = allAddOns.filter(p =>
        !p.category?.includes('Automation') && !p.category?.includes('Jacob')
      );

      const automationProducts = allAddOns.filter(p =>
        p.category?.includes('Automation')
      );

      // Sort regular add-ons in custom order
      const sortedAddOns = regularAddOns.sort((a, b) => {
        const order = [
          'Manual Dosed Discharger Set | Add-on',
          'Filling Support Bars | Round | Add-on',
          'Filling Support Bars | Round | Add-on | Stainless Steel',
          'Vacuum Container | Add-on',
          'Dust Free Pipe | Add-on',
          'Granulate Hopper | Unloading Station | Add-on',
          'Big Bag Piercer'
        ];

        const indexA = order.indexOf(a.name);
        const indexB = order.indexOf(b.name);

        // If both products are in the custom order, sort by their order
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }

        // If only one is in the custom order, prioritize it
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;

        // If neither is in the custom order, maintain original order
        return 0;
      });

      // Filter out hidden products
      const hiddenProducts = [
        'Filling Support Bars | Round | Add-on',
        'Filling Support Bars | Round | Add-on | Stainless Steel',
        'Vacuum Container | Add-on',
        'Dust Free Pipe | Add-on'
      ];

      const visibleAddOns = sortedAddOns.filter(product =>
        !hiddenProducts.includes(product.name)
      );

      setAddOns(visibleAddOns);
      setAutomationAddOns(automationProducts);
    }
  }, [selectedMainCategory, organizedProducts]);

  // Re-render when price mode changes
  useEffect(() => {
    // This empty effect will trigger re-render when priceMode changes
  }, [priceMode]);

  // Get the currently selected product object
  const productObject = selectedProduct && products.find(p => p.id === selectedProduct);

  // Auto-expand next section when user makes a selection
  useEffect(() => {
    if (selectedProduct && productObject) {
      const currentType = productObject.attributes?.type;

      // If Frame Base is selected, auto-expand Feet Set section
      if (currentType === 'Frame Base' && !expandedSections.has(2)) {
        setExpandedSections(prev => new Set([...prev, 2]));
      }
      // If Feet Set is selected, auto-expand Add-ons section
      else if (currentType === 'Feet Set' && !expandedSections.has(3)) {
        setExpandedSections(prev => new Set([...prev, 3]));
      }
    }
  }, [selectedProduct, productObject]);

  // Toggle section expand/collapse
  const toggleSection = (sectionNumber) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionNumber)) {
        newSet.delete(sectionNumber);
      } else {
        newSet.add(sectionNumber);
      }
      return newSet;
    });
  };

  // Check if a section has a selected product
  const sectionHasSelection = (sectionProducts) => {
    return sectionProducts.some(p => p.id === selectedProduct);
  };
  
  // Check if selected product is a frame base to show dependency message
  const showFrameBaseDependencyMessage = productObject && productObject.attributes?.type === 'Frame Base';
  
  // Handle product selection - use standard single product selection
  const handleProductSelect = (productId) => {
    setSelectedProduct(productId);
    calculateQuote();
  };

  // Handle quick add to quote
  const handleQuickAdd = (product, e) => {
    e.stopPropagation(); // Prevent card selection
    
    // Set quick add state for visual feedback
    setQuickAddStates(prev => ({
      ...prev,
      [product.id]: true
    }));
    
    // Call the new incremental quick add function
    quickAddToQuote(product.id, selectedMainCategory);
    
    // Reset quick add state after animation
    setTimeout(() => {
      setQuickAddStates(prev => ({
        ...prev,
        [product.id]: false
      }));
    }, 1500);
  };
  
  // Image modal handlers
  const handleOpenImageModal = (imageSrc, productName, productId) => {
    setImageModalData({
      imageSrc,
      productName,
      productId
    });
  };
  
  const handleCloseImageModal = () => {
    setImageModalData(null);
  };
  
  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && imageModalData) {
        handleCloseImageModal();
      }
    };

    if (imageModalData) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [imageModalData]);

  // Auto-scroll to product summary when a product is selected
  useEffect(() => {
    if (selectedProduct && productSummaryRef.current) {
      // Small delay to ensure DOM has rendered
      setTimeout(() => {
        productSummaryRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }, 300);
    }
  }, [selectedProduct]);
  
  // If not viewing unloading stations, don't render
  if (selectedMainCategory !== 'unloading_stations' || !products.length) {
    return <EmptyState>Please select the Unloading Stations category to view options</EmptyState>;
  }

  const renderProductGrid = (productsToRender, title, step) => {
    const isExpanded = expandedSections.has(step);
    const hasSelection = sectionHasSelection(productsToRender);

    return (
      <Section>
        <SectionHeader
          onClick={() => toggleSection(step)}
          $highlighted={hasSelection}
          $isExpanded={isExpanded}
        >
          <SectionNumber $completed={hasSelection}>
            {hasSelection ? <Check size={18} /> : step}
          </SectionNumber>
          <SectionTitle>{title}</SectionTitle>
          <ChevronIcon $isExpanded={isExpanded} />
        </SectionHeader>
        <AnimatePresence initial={false}>
          {isExpanded && (
            <AccordionContent
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <ProductGrid>
        {productsToRender.map(product => {
          // Use standard single product selection
          const isSelected = selectedProduct === product.id;
          
          // Get partner or client price based on current mode
          const getPrice = () => {
            if (!product.quantities || product.quantities.length === 0) {
              return product.basePrice;
            }
            
            const firstTier = product.quantities[0];
            return priceMode === 'partner' ? firstTier.partnerPrice : firstTier.clientPrice;
          };
          
          const price = getPrice();
          
          const imageSrc = getProductImageSrc(product, 'unloading_stations');

          return (
            <ProductCard
              key={product.id}
              selected={isSelected}
              onClick={() => handleProductSelect(product.id)}
              onMouseEnter={() => setHoveredCard(product.id)}
              onMouseLeave={() => setHoveredCard(null)}
              whileHover={{ y: -4 }}
              whileTap={{ scale: 0.98 }}
            >
              <ProductImageContainer>
                <ProductImage>
                  <img src={imageSrc} alt={product.name} />
                </ProductImage>
                <EnlargeButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenImageModal(imageSrc, product.name, product.id);
                  }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  ⛶
                </EnlargeButton>
                {isSelected && (
                  <SelectedCheck
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring" }}
                  >
                    ✓
                  </SelectedCheck>
                )}
              </ProductImageContainer>
              
              <ProductInfo>
                <ProductName>
                  {product.name
                    .replace('Unloading Station ', '')
                    .replace('Frame Base', 'Base Frame')}
                </ProductName>
                <ProductPrice>
                  <span>{priceMode === 'partner' ? 'Partner' : 'Client'}:</span>
                  {formatCurrency(price)}
                </ProductPrice>
                <QuickAddButton
                  hovered={hoveredCard === product.id}
                  added={quickAddStates[product.id]}
                  onClick={(e) => handleQuickAdd(product, e)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0.8 }}
                  animate={{ 
                    opacity: hoveredCard === product.id ? 1 : 0.8,
                    scale: quickAddStates[product.id] ? 1.15 : 1
                  }}
                  transition={{ type: "spring", stiffness: 200, damping: 15 }}
                >
                  +
                </QuickAddButton>
              </ProductInfo>
            </ProductCard>
          );
        })}
              </ProductGrid>
            </AccordionContent>
          )}
        </AnimatePresence>
      </Section>
    );
  };
  
  return (
    <div>
      {renderProductGrid(frameBases, "Select a Frame Base", 1)}
      
      {/* Show dependency message when frame base is selected */}
      <AnimatePresence>
        {showFrameBaseDependencyMessage && (
          <DependencyMessage
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <GuidanceIcon>💡</GuidanceIcon>
            <MessageText>A complete Modular Unloading Station requires both a Frame Base and a Feet Set</MessageText>
          </DependencyMessage>
        )}
      </AnimatePresence>
      
      {renderProductGrid(feetSets, "Select a Feet Set", 2)}
      {renderProductGrid(addOns, "Select Add-ons", 3)}
      {renderProductGrid(automationAddOns, "Select Automation Add-ons", 4)}

      {/* Product Summary */}
      <AnimatePresence>
        {selectedProduct && productObject && (
          <motion.div
            ref={productSummaryRef}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <ProductSummary>
              <SummaryHeader>
                <SummaryTitle>📦 Product Summary</SummaryTitle>
              </SummaryHeader>
              <SummaryContent>
                <ProductDetails>
                  <DetailImage>
                    <img src={getProductImageSrc(productObject, 'unloading_stations')} alt={productObject.name} />
                    <EnlargeButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenImageModal(getProductImageSrc(productObject, 'unloading_stations'), productObject.name, productObject.id);
                      }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      ⛶
                    </EnlargeButton>
                  </DetailImage>
                  <DetailInfo>
                    <DetailName>{productObject.name}</DetailName>
                    <DetailCode>Item #{productObject.id}</DetailCode>
                    <FeatureList>
                      {extractProductFeatures(productObject.name).map((feature, index) => (
                        <Feature key={index}>{feature}</Feature>
                      ))}
                    </FeatureList>
                  </DetailInfo>
                </ProductDetails>
                <QuantitySelector />
              </SummaryContent>
            </ProductSummary>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Image Modal */}
      <AnimatePresence>
        {imageModalData && (
          <ImageModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseImageModal}
          >
            <ImageModalContent
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 15, stiffness: 200 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ImageModalCloseButton onClick={handleCloseImageModal}>
                ×
              </ImageModalCloseButton>
              
              {typeof imageModalData.imageSrc === 'string' && imageModalData.imageSrc.startsWith('http') ? (
                <FullscreenImage 
                  src={imageModalData.imageSrc} 
                  alt={imageModalData.productName}
                />
              ) : (
                <div style={{ 
                  padding: '4rem', 
                  fontSize: '4rem', 
                  textAlign: 'center',
                  background: 'white'
                }}>
                  {imageModalData.imageSrc}
                </div>
              )}
              
              <ImageModalHeader>
                <ImageModalTitle>{imageModalData.productName}</ImageModalTitle>
                <ImageModalCode>Item #{imageModalData.productId}</ImageModalCode>
              </ImageModalHeader>
            </ImageModalContent>
          </ImageModalOverlay>
        )}
      </AnimatePresence>
    </div>
  );
}

export default UnloadingStationConfigurator;
